import type { AssistantTask, TaskAutomation } from '@crowdex/agent-contracts/api';
import type {
  AssistantConversationApi,
  AssistantMemberView,
  AssistantOverview,
} from './assistant-conversation-api';

/**
 * The project of the boards (D65, D67, mobile 2e): « Crowdex Mobile », its saved agents and its
 * tasks, for `/desktop-preview…` and component tests. Never used outside the preview.
 */
export const PREVIEW_PROJECT_CONVERSATION = '0b7e3c52-6a8d-4c6f-9a51-2f6d1e0a7b11';

const id = (n: number) => `7a0806${String(n).padStart(2, '0')}-0000-4000-8000-000000000000`;
const TEAM_TASK = id(10);
const SWIPE_TASK = id(11);
// The conversation the boards show (`PREVIEW_THREADS`): the sidebar's running row is this task.
const INSPIRATION_TASK = 'preview-active';
const EXTENSIONS_TASK = id(13);
const COPY_TASK = id(14);

/** The user's automations a task may run, as the boards' task provider lists them. */
export const PREVIEW_AUTOMATIONS: TaskAutomation[] = [
  { id: 'quick', name: 'Quick', summary: 'Develop, PR' },
  { id: 'standard', name: 'Standard', summary: 'Develop, review ×3, PR', default: true },
  { id: 'careful', name: 'Careful', summary: 'Standard, then you approve' },
  { id: 'work-as-a-team', name: 'Work as a team', summary: 'A lead plans waves, then review' },
];

export const PREVIEW_PERSONAS: AssistantMemberView[] = [
  { agentId: 'developer', name: 'Developer', agent: 'claude-code' },
  { agentId: 'reviewer', name: 'Reviewer', agent: 'codex' },
  { agentId: 'designer', name: 'Designer', agent: 'claude-code' },
];

const run = (
  workflowId: string,
  step: string | null,
  state: AssistantTask['workflowRun']['state'],
  costUsd: number | null,
  round: { current: number; max: number } | null = null,
): AssistantTask['workflowRun'] => ({
  workflowId,
  automationName: PREVIEW_AUTOMATIONS.find((entry) => entry.id === workflowId)?.name ?? null,
  definition: `.crowdex/automations/${workflowId}.json`,
  runId: `run-${workflowId}`,
  step,
  round,
  state,
  costUsd,
});

export const PREVIEW_PROJECT_TASKS: AssistantTask[] = [
  {
    parentConversationId: PREVIEW_PROJECT_CONVERSATION,
    mine: true,
    conversationId: TEAM_TASK,
    title: 'Mobile navigation',
    workflowRun: {
      ...run('work-as-a-team', 'Each wave, in order', 'running', 0.3),
      // What its lead planned: wave 1 under way, wave 2 after it (D65's plan card). Each item's
      // conversation has its title (one name per task).
      plan: {
        goal: 'Mobile navigation',
        wave: { current: 1, total: 2 },
        items: [
          {
            title: 'Mobile interface inspiration',
            state: 'running',
            conversationId: INSPIRATION_TASK,
            wave: 1,
          },
          {
            title: 'Navigation swipe gestures',
            state: 'waiting',
            conversationId: SWIPE_TASK,
            wave: 1,
          },
          {
            title: 'Extensions cloud mobile issues',
            state: 'running',
            conversationId: EXTENSIONS_TASK,
            wave: 1,
          },
          {
            title: 'Drawer copy in French',
            state: 'next',
            conversationId: COPY_TASK,
            wave: 1,
          },
          { title: 'Android APK download page', state: 'later', wave: 2 },
        ],
      },
    },
    branch: null,
    pullRequest: null,
    children: [],
  },
  {
    parentConversationId: PREVIEW_PROJECT_CONVERSATION,
    mine: true,
    conversationId: INSPIRATION_TASK,
    title: 'Mobile interface inspiration',
    workflowRun: run('standard', 'Open a pull request', 'running', 1.1),
    branch: 'ccr-2368d65e',
    pullRequest: { number: 556, state: 'open' },
    children: [],
  },
  {
    parentConversationId: PREVIEW_PROJECT_CONVERSATION,
    mine: true,
    conversationId: SWIPE_TASK,
    title: 'Navigation swipe gestures',
    workflowRun: run('careful', 'Develop', 'waiting', 0.6),
    branch: 'ccr-swipe-back',
    pullRequest: null,
    children: [],
  },
  {
    parentConversationId: PREVIEW_PROJECT_CONVERSATION,
    mine: true,
    conversationId: EXTENSIONS_TASK,
    title: 'Extensions cloud mobile issues',
    workflowRun: run('standard', 'Review', 'running', 1.4, { current: 2, max: 3 }),
    branch: 'ccr-ext-cloud',
    pullRequest: { number: 552, state: 'open' },
    children: [
      {
        conversationId: id(15),
        title: 'Review',
        agentName: 'Codex',
        costUsd: 0.3,
      },
    ],
  },
  {
    parentConversationId: PREVIEW_PROJECT_CONVERSATION,
    mine: true,
    conversationId: COPY_TASK,
    title: 'Drawer copy in French',
    workflowRun: run('quick', null, 'running', null),
    branch: null,
    pullRequest: null,
    children: [],
  },
];

/** The boards' project, in memory. */
export function previewAssistantConversationApi(): AssistantConversationApi {
  const overview: AssistantOverview = {
    repositories: [
      { id: 'repo-crowdex', name: 'PersoJonas/crowdex' },
      { id: 'repo-site', name: 'PersoJonas/site' },
    ],
    yours: true,
    owner: 'Jonas',
    automations: PREVIEW_AUTOMATIONS,
  };
  return {
    overview: async () => structuredClone(overview),
    tasks: async () => structuredClone(PREVIEW_PROJECT_TASKS),
  };
}
